import { css, html, LitElement } from "lit";

import "../components/navbar";
import "../components/flightmap";
import "../components/discorduser";

import { FLIGHT_SAMPLE_INDEX } from "../../constants";
import { fetchFlightSessionById } from "../common/api";
import { setTitle, trackPage } from "../common/client";
import { typeToHexMap } from "../common/hexmap";
import { websiteStyles } from "../styles/website";

/**
 * @param {number} startedAt
 * @param {number} endedAt
 * @returns {string}
 */
function formatDuration(startedAt, endedAt) {
  if (!startedAt) {
    return "-";
  }
  const end = endedAt || Date.now();
  const totalSeconds = Math.max(0, Math.floor((end - startedAt) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
}

export class FlightPage extends LitElement {
  static properties = {
    sessionId: { type: String },
    flightSession: { type: Object },
  };

  static styles = [
    websiteStyles,
    css`
      :host {
        display: block;
        width: 100vw;
        min-height: 100vh;
        background: #fff;
        color: #0f172a;
        padding-top: 70px;
      }

      .content {
        text-align: left;
      }

      .summary {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;
        margin-bottom: 16px;
      }

      .card {
        border: 1px solid #d4dee8;
        border-radius: 10px;
        padding: 10px;
        background: #f7fafc;
      }

      .label {
        font-size: 12px;
        color: #64748b;
        text-transform: uppercase;
        margin-bottom: 6px;
      }

      .value {
        font-size: 18px;
        font-weight: 700;
      }

      .details {
        margin-top: 18px;
        font-size: 14px;
        color: #334155;
        line-height: 1.7;
      }

      .aircraft-image {
        max-width: 100px; 
        border-radius: 8px; 
        position: absolute; 
        right: 30px; 
        top: 40px;
      }

      discord-user-component {
        display: inline-block;
        vertical-align: middle;
        margin-left: 8px;
      }

      @media (max-width: 900px) {
        .summary {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      @media (max-width: 600px) {
        .summary {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        h1 {
            margin: 0;
        }
        .aircraft-image{
          top: 20px;
        }
      }
    `,
  ];

  constructor() {
    super();
    this.sessionId = "";
    this.flightSession = null;
    this.timer = null;
    this.aircraftURL = "";
  }

  connectedCallback() {
    super.connectedCallback();

    this.sessionId = window.location.pathname.split("/").pop() || "";

    setTitle("Flight Session");
    trackPage();

    this.loadData();
    this.timer = setInterval(() => {
      if (this.flightSession?.active) {
        this.loadData();
      }
    }, 5000);
  }

  async loadData() {
    if (!this.sessionId) {
      return;
    }
    this.flightSession = await fetchFlightSessionById(this.sessionId);
    if (this.flightSession?.aircraftIcao) {
      this.aircraftURL = await this.getAircraftPictureBasedOnIcao(
        this.flightSession.aircraftIcao
      );
    }
    this.requestUpdate();
  }

  disconnectedCallback() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    super.disconnectedCallback();
  }

  /**
   * Fetches an aircraft picture URL based on the ICAO code using the PlaneSpotters API.
   * @param {string} icao - The ICAO code of the aircraft.
   * @returns {Promise<string>} - A promise that resolves to the URL of the aircraft picture, or an empty string if not found.
   */
  async getAircraftPictureBasedOnIcao(icao) {
    const hexCode = typeToHexMap[icao] || "000000";
    const url = `https://api.planespotters.net/pub/photos/hex/${hexCode}`;
    //fetch the json at the URL and get the thumbnail_large.src property, if it exists, otherwise return blank
    const data = await fetch(url).then((res) => res.json());
    return data?.photos?.[0]?.thumbnail_large?.src || "";
  }

  render() {
    const session = this.flightSession;

    if (!session) {
      return html`
        <navbar-component></navbar-component>
        <section>
          <div class="content">
            <h1>Flight Session</h1>
            <p>Loading flight data...</p>
          </div>
        </section>
      `;
    }

    const samples = session.samples || [];
    const lastSample = samples[samples.length - 1] || [];

    const avgSpeed = samples.length
      ? samples.reduce(
          (/** @type {number} */ acc, /** @type {number[]} */ sample) =>
            acc + (sample[FLIGHT_SAMPLE_INDEX.SPEED] || 0),
          0
        ) / samples.length
      : 0;

    return html`
      <navbar-component></navbar-component>
      <section>
        <div class="content" style="position: relative;">
          <h1>
            ${session.callsign || session.aircraftIcao || session.username || ""}
          </h1>

          <p>
            ${session.aircraftIcao || "Unknown aircraft"} <discord-user-component .userID=${session.userid}></discord-user-component>
          </p>

          ${this.aircraftURL ? html`<img src="${this.aircraftURL}" alt="Aircraft Image" class="aircraft-image">` : ""}

          <div class="summary">
            <div class="card">
              <div class="label">Distance</div>
              <div class="value">${Math.round(session.distanceNm || 0).toLocaleString()} NM</div>
            </div>
            <div class="card">
              <div class="label">Duration</div>
              <div class="value">${formatDuration(session.startedAt, session.endedAt)}</div>
            </div>
            <div class="card">
              <div class="label">Status</div>
              <div class="value" style="text-transform: uppercase;color: ${session.active ? "#09844b" : "#333"}">${session.active ? "Active" : "Finished"}</div>
            </div>
            <div class="card">
              <div class="label">Max Altitude</div>
              <div class="value">${Math.round(session.maxAltFt || lastSample[FLIGHT_SAMPLE_INDEX.ALT] || 0).toLocaleString()} ft</div>
            </div>
            <div class="card">
              <div class="label">Max Speed</div>
              <div class="value">${Math.round(session.maxSpeedKt || 0).toLocaleString()} kt</div>
            </div>
            <div class="card">
              <div class="label">Avg Speed</div>
              <div class="value">${Math.round(avgSpeed).toLocaleString()} kt</div>
            </div>
          </div>

          <flight-map-component .samples=${samples} .liveMode=${this.flightSession.active}></flight-map-component>

        </div>
      </section>
    `;
  }
}

customElements.define("flight-page", FlightPage);
